import { Alert, Box, Button, Card, CardContent, Divider, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import NavBarLayout from "../../components/nav-bar-layout";
import { Delete, Lock, Logout, Person, Save, School, Visibility, VisibilityOff } from "@mui/icons-material";
import { useStudentActions } from "../../hooks/useStudentActions";
import { useEffect, useState } from "react";
import { newStudent, Student } from "../../models/student";
import { useSnackbar } from "../../components/snackbar";
import renderAvatar from "../../components/render-avatar";
import BackdropLoader from "../../components/backdrop-loader";
import LeaveClassroomDialog from "./unenroll-classroom-dialog";

 const formatDate = (dateString: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

export default function Account() {
    const { 
        student, 
        getStudent, 
        updateStudent,
        classrooms,
        getClassrooms,
        unEnroll,
        logOut
    } = useStudentActions();

    const [locStudent, setLocStudent] = useState<Student>(newStudent());

    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);

    const [leaveClassroomDialog, setLeaveClassroomDialog] = useState(false);
    const [leaving, setLeaving] = useState(false);

    
    const [loading, setLoading] = useState(false);

    const { showMessage } = useSnackbar();

    const [selectedClassroomId, setSelectedClassroomId] = useState(0);


    const [states, setStates] = useState<any[]>([]);
    const [lgas, setLgas] = useState<any[]>([]);

    const handleStudentChange = <K extends keyof Student>(field: K, value: Student[K]) => {
        setLocStudent((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    // const isPasswordValid = (password: string) => {
    //   return /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/.test(password);
    // };

    const _updateStudent = async (action: number) => {
        const profilePayload = {
            phone_number: locStudent.phone_number,
            state: locStudent.state,
            lga: locStudent.lga
        };

        const passwordPayload = {
            password: locStudent.password,
            password_repeat: locStudent.password_repeat
        };

        setLoading(true);
        await updateStudent(action === 0 ? profilePayload : passwordPayload, action);
        setLoading(false);
    };

    const leaveClassroom = async () => {
        if (selectedClassroomId === 0) return;
        
        setLeaving(true);
        await unEnroll(selectedClassroomId);
        setLeaveClassroomDialog(false);
        setLeaving(false);
    };

    useEffect(() => {
        fetch("https://vapi.verifyme.ng/v1/countries/1/states")
        .then((response) => response.json())
        .then((data) => {
            if (data.status === "success") {
            setStates(data.data);
            }
        });
    }, []);

    useEffect(() => {
        if (locStudent.state !== 0) {
        fetch(`https://vapi.verifyme.ng/v1/states/${locStudent.state}/lgas`)
            .then((response) => response.json())
            .then((data) => {
            if (data.status === "success") {
                setLgas(data.data);
            }
            });
        }
    }, [locStudent.state]);

    useEffect(() => {
      if (student) {
        setLocStudent(student);   
      }
    }, [student]);

    useEffect(() => {
        const loadStudent = async () => {
            await getStudent();
        };

        if (!student) {
            loadStudent();
        }
    }, []);
    

    return (
        <NavBarLayout>
            {!student && <BackdropLoader/>}
            <Stack spacing={3}>
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2
                    }}
                >
                    <Person sx={{ color: 'primary.main' }}/>
                    Basic Details
                </Typography>

                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 6}}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            value={locStudent.name}
                            onChange={(e) => handleStudentChange("name", e.target.value)}
                            sx={{
                                '& .MuiInputBase-root': {
                                    backgroundColor: 'white'
                                }
                            }}
                            disabled
                        />
                    </Grid>

                    <Grid size={{xs: 12, md: 6}}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            type="email"
                            value={locStudent.email}
                            disabled
                            sx={{
                                '& .MuiInputBase-root': {
                                    backgroundColor: 'white'
                                }
                            }}
                        />
                    </Grid>

                    <Grid size={{xs: 12, md: 6}}>
                        <TextField
                            fullWidth
                            label="Phone Number"
                            variant="outlined"
                            type="tel"
                            disabled
                            value={locStudent.phone_number}
                            onChange={(e) => handleStudentChange("phone_number", e.target.value)}
                            sx={{
                                '& .MuiInputBase-root': {
                                    backgroundColor: 'white'
                                }
                            }}
                        />
                    </Grid>

                    <Grid size={{xs: 12, md: 4}} >
                        <TextField
                            fullWidth
                            label="Country"
                            variant="outlined"
                            value={locStudent.country || "Nigeria"}
                            disabled
                            sx={{
                                '& .MuiInputBase-root': {
                                    backgroundColor: 'white'
                                }
                            }}
                        />
                    </Grid>

                    <Grid size={{xs: 12, md: 4}} >
                        <FormControl fullWidth>
                            <InputLabel>State</InputLabel>

                            <Select
                                value={locStudent.state || ""}
                                disabled
                                onChange={(e) => {
                                    const stateId = Number(e.target.value);
                                    handleStudentChange("state", stateId);
                                    handleStudentChange("lga", 0);
                                }}
                                label="State"
                                sx={{
                                    backgroundColor: '#fff'
                                }}
                            >
                                <MenuItem value={0}>
                                    <em>Select State</em>
                                </MenuItem>
                                
                                {states.map((state) => (
                                    <MenuItem key={state.id} value={state.id}>
                                        {state.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    <Grid size={{xs: 12, md: 4}} >
                        <FormControl fullWidth disabled={locStudent.state === 0}>
                            <InputLabel>Local Government Area</InputLabel>

                            <Select
                                value={locStudent.lga || ""}
                                disabled
                                onChange={(e) => handleStudentChange("lga", Number(e.target.value))}
                                label="Local Government Area"
                                sx={{
                                    backgroundColor: '#fff'
                                }}
                            >
                                <MenuItem value={0}>
                                    <em>Select LGA</em>
                                </MenuItem>

                                {lgas.map((lga) => (
                                    <MenuItem key={lga.id} value={lga.id}>
                                        {lga.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
        
                </Grid>

                <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={() => _updateStudent(0)}
                        // disabled={loading || !locStudent.name || !locStudent.phone_number}
                        disabled
                        sx={{
                            px: 3,
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            transform: 'translateY(-1px)'
                            },
                            '&:active': {
                            transform: 'translateY(0)'
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        Save Changes
                    </Button>
                </Box>
                    
          
       
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2
                    }}
                >
                    <Lock sx={{ color: 'primary.main' }}/>
                    Change Password
                </Typography>

                <Divider sx={{ mb: 3 }} />

                <Alert severity="info" sx={{ mb: 3 }}>
                    Ensure your password is at least 8 characters long and contains a mix of letters and numbers.
                </Alert>

                <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 6}}>
                        <TextField
                            fullWidth
                            label="Current Password"
                            variant="outlined"
                            type={showPassword ? "text" : "password"}
                            value={locStudent.password}
                            onChange={(e) => handleStudentChange("password", e.target.value)}
                            sx={{
                                '& .MuiInputBase-root': {
                                    backgroundColor: 'white'
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                ),
                            }}
                        />
                    </Grid>

                    <Grid size={{xs: 12, md: 6}}>
                        <TextField
                            fullWidth
                            label="New Password"
                            variant="outlined"
                            type={showNewPassword ? "text" : "password"}
                            value={locStudent.password_repeat}
                            onChange={(e) => handleStudentChange("password_repeat", e.target.value)}
                            sx={{
                                '& .MuiInputBase-root': {
                                    backgroundColor: 'white'
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        edge="end"
                                    >
                                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                        variant="contained"
                        startIcon={<Lock />}
                        onClick={() => _updateStudent(1)}
                        disabled={loading || !locStudent.password || !locStudent.password_repeat}
                        sx={{
                            px: 3,
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                transform: 'translateY(-1px)'
                            },
                            '&:active': {
                                transform: 'translateY(0)'
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        Update Password
                    </Button>
                </Box>

                 <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2
                    }}
                >
                    <School sx={{ color: 'primary.main' }}/>
                    Classrooms
                </Typography>

                <Divider sx={{ mb: 3 }} />

               {Object.values(classrooms || {}).sort((a, b) => new Date(b.enrollment_date).getTime() - new Date(a.enrollment_date).getTime())
                .map((classroom) => {

                    const enrollmentDate = formatDate(classroom.enrollment_date);
                    const expiryDate = classroom.expiry_date ? formatDate(classroom.expiry_date) : null;

                    return (
                        <Box 
                            key={classroom.id}
                            sx={{ 
                                display: 'flex', 
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: { xs: 1.5, sm: 2 },
                                padding: { xs: 1.5, sm: 2 },
                                border: '1px solid',
                                borderColor: 'grey.200',
                                borderRadius: 2,
                                backgroundColor: 'white',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: 'grey.300',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                    transform: 'translateY(-1px)'
                                }
                            }}
                        >
                            <Box sx={{ 
                                display: { xs: 'flex', sm: 'contents' },
                                alignItems: 'center',
                                gap: 2,
                                width: { xs: '100%', sm: 'auto' },
                                justifyContent: { xs: 'space-between', sm: 'flex-start' }
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                                    {renderAvatar(classroom.id, classroom.name, classroom.image, 40, 48)}
                                    
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: { xs: '0.9rem', sm: '0.95rem' },
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            color: 'text.primary',
                                            flex: 1,
                                            minWidth: 0
                                        }}
                                    >
                                        {classroom.name}
                                    </Typography>
                                </Box>
                                
                                <Button
                                    onClick={() => {
                                        setSelectedClassroomId(classroom.id);
                                        setLeaveClassroomDialog(true);
                                    }}
                                    variant="outlined"
                                    size="small"
                                    color="error"
                                    sx={{
                                        minWidth: 'auto',
                                        padding: { xs: '4px 12px', sm: '6px 16px' },
                                        fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: 1.5,
                                        flexShrink: 0,
                                        '&:hover': {
                                            backgroundColor: 'error.light',
                                            color: 'white',
                                            transform: 'scale(1.05)'
                                        },
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    Leave
                                </Button>
                            </Box>
                            
                            {/* Date badges section */}
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                width: { xs: '100%', sm: 'auto' },
                                flexWrap: 'wrap',
                                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                                paddingLeft: { xs: 7, sm: 0 } // Align with text on mobile
                            }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        backgroundColor: 'grey.50',
                                        padding: '2px 8px',
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'grey.200',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <span>Enrolled:</span>
                                    <span style={{ fontWeight: 500, color: '#059669' }}>
                                        {enrollmentDate}
                                    </span>
                                </Typography>
                                
                                {expiryDate && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            backgroundColor: 'grey.50',
                                            padding: '2px 8px',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'grey.200',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        <span>Expires:</span>
                                        <span style={{ fontWeight: 500, color: '#dc2626' }}>
                                            {expiryDate}
                                        </span>
                                    </Typography>
                                )}
                                
                                {!expiryDate && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                            backgroundColor: 'green.50',
                                            color: 'green.700',
                                            padding: '2px 8px',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'green.200',
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        No expiry
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    );
                })}

                <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Logout />}
                    onClick={logOut}
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        padding: '8px 16px',
                        fontSize: '0.875rem',
                        border: '1.5px solid',
                        borderColor: 'error.main',
                        color: 'error.main',
                        '&:hover': {
                            backgroundColor: 'error.main',
                            color: 'white',
                            borderColor: 'error.main',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                        },
                        '&:active': {
                            transform: 'translateY(0)',
                        },
                        transition: 'all 0.2s ease'
                    }}
                >
                    Logout
                </Button>

            </Stack>
            
            <LeaveClassroomDialog
                open={leaveClassroomDialog}
                setOpen={setLeaveClassroomDialog}
                leaveFunction={leaveClassroom}
            />
            
            {leaving && <BackdropLoader/>}
        </NavBarLayout>
    );
}